
import React, { useState, useCallback, useEffect } from 'react';
import Layout from './components/Layout';
import TruckForm from './components/TruckForm';
import RouteTable from './components/RouteTable';
import LogicExplorer from './components/LogicExplorer';
import LoadingAlert from './components/LoadingAlert';
import WarehouseMap from './components/WarehouseMap';
import { AppStatus, ComputeParams, Route } from './types';
import { INITIAL_LOCATION } from './constants';
import { initializeRequesterId } from './utils/storageManager';
import { loopService } from './api';
import { GenerateLoopResponse, RequestDetails } from './api/types';
import { useLoading } from './context/LoadingContext';

const App: React.FC = () => {
  const { setLoading, withLoading } = useLoading();

  useEffect(() => {
    initializeRequesterId();
  }, []);

  // Check for existing loop on page load
  useEffect(() => {
    const checkExistingLoop = async () => {
      try {
        const result = await loopService.getRequestDetails();
        if (result.status === 200 && result.data && result.data.vehicleId) {
          setRequestDetails(result.data);
          setStatus(AppStatus.REQUEST_DETAILS);
        }
      } catch {
        // Silently ignore — stay on IDLE
      }
    };
    checkExistingLoop();
  }, []);

  const [activeTab, setActiveTab] = useState<'dashboard' | 'logic'>('dashboard');
  const [status, setStatus] = useState<AppStatus>(AppStatus.IDLE);
  const [params, setParams] = useState<ComputeParams>({
    truckId: '',
    driverId: '',
    vehicleLat: INITIAL_LOCATION.lat,
    vehicleLong: INITIAL_LOCATION.lng,
    driverLat: INITIAL_LOCATION.lat,
    driverLong: INITIAL_LOCATION.lng,
    date: new Date().toISOString().split('T')[0],
    time: '08:00',
  });
  const [results, setResults] = useState<Route[]>([]);
  const [logs, setLogs] = useState<string[]>([]);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [toast, setToast] = useState<string | null>(null);
  const [showMap, setShowMap] = useState(false);
  const [requestDetails, setRequestDetails] = useState<RequestDetails | null>(null);
  const [loadingLoopDetails, setLoadingLoopDetails] = useState(false);

  const handleParamChange = useCallback((updates: Partial<ComputeParams>) => {
    setParams(prev => ({ ...prev, ...updates }));
  }, []);

  // Step 1: Generate loop + fetch request details
  const handleCompute = async () => {
    setStatus(AppStatus.LOADING);
    setErrorMsg('');
    setResults([]);
    setLogs([]);
    setRequestDetails(null);

    try {
      const response: GenerateLoopResponse = await withLoading(async () =>
        loopService.generateLoop({
          vehicleId: params.truckId,
          vehicleLat: params.vehicleLat,
          vehicleLong: params.vehicleLong,
          driverId: params.driverId,
          driverLat: params.driverLat,
          driverLong: params.driverLong,
          date_time: `${params.date} ${params.time}`,
        })
      );

      if (response.status !== 201) {
        setStatus(AppStatus.ERROR);
        const errDetail = typeof response.data === 'string' ? response.data : 'Logic engine found no viable routes.';
        setErrorMsg(errDetail);
        return;
      }

      // After successful loop generation, fetch request details
      const reqDetails = await withLoading(async () =>
        loopService.getRequestDetails()
      );

      if (reqDetails.status === 200 && reqDetails.data) {
        setRequestDetails(reqDetails.data);
        setStatus(AppStatus.REQUEST_DETAILS);
      } else {
        setStatus(AppStatus.ERROR);
        setErrorMsg('Failed to fetch request details after loop generation.');
      }
    } catch (err: any) {
      setStatus(AppStatus.ERROR);
      setErrorMsg('Critical connection failure in backend routing service.');
    }
  };

  // Step 2: Get loop details (triggered by button click)
  const handleGetLoopDetails = async () => {
    if (!requestDetails?.vehicleId) return;
    setLoadingLoopDetails(true);

    try {
      const loopDetails = await withLoading(async () =>
        loopService.getLoopDetails({ vehicleId: requestDetails.vehicleId })
      );

      if (loopDetails.status !== 200 || !loopDetails.data?.message) {
        setStatus(AppStatus.ERROR);
        setErrorMsg('Failed to fetch loop details.');
        return;
      }

      const routes: Route[] = Object.entries(loopDetails.data.message).map(
        ([loopId, detail]) => ({
          pickupDate: detail.pickupDate || '',
          productId: detail.productIds || [],
          sourceCustomerWarehouseId: detail.originWarehouse || '',
          sourcePredictedCity: detail.originCity || '',
          destinationCustomerWarehouseId: detail.destinationWarehouse || '',
          destinationPredictedCity: detail.destinationCity || '',
          units: 0,
          currentLoadingMode: '',
          tonnage: detail.tonnage ?? 0,
          volume: detail.volume ?? 0,
          distance: 0,
          deliveryDate: detail.deliveryDate || '',
          activity: detail.activity || '',
        })
      );

      setResults(routes);
    } catch (err: any) {
      setStatus(AppStatus.ERROR);
      setErrorMsg('Failed to fetch loop details.');
    } finally {
      setLoadingLoopDetails(false);
    }
  };

  const triggerToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  return (
    <Layout>
      <LoadingAlert />
      <WarehouseMap isOpen={showMap} onClose={() => setShowMap(false)} />

      {/* Toast Notification */}
      {toast && (
        <div className="fixed top-20 right-6 z-50 animate-in slide-in-from-right duration-300">
          <div className="px-4 py-3 rounded-lg shadow-2xl text-sm font-medium flex items-center gap-3" style={{ backgroundColor: 'var(--ih-theme-bg-card)', color: 'var(--ih-theme-text-primary)', borderWidth: '1px', borderColor: 'var(--ih-theme-border-light)' }}>
            <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: 'var(--ih-theme-accent)' }}></div>
            {toast}
          </div>
        </div>
      )}

      {activeTab === 'dashboard' ? (
        <>
          <div className="flex flex-col gap-6">
            <TruckForm
              params={params}
              onChange={handleParamChange}
              onCompute={handleCompute}
              isLoading={status === AppStatus.LOADING}
              onToast={triggerToast}
              onShowMap={() => setShowMap(true)}
            />
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-xl shadow-sm overflow-hidden h-full flex flex-col" style={{ backgroundColor: 'var(--ih-theme-bg-card)', borderWidth: '1px', borderColor: 'var(--ih-theme-border-light)' }}>
              <div className="px-6 py-4 flex items-center justify-between" style={{ borderBottomWidth: '1px', borderColor: 'var(--ih-theme-border-light)' }}>
                <div>
                  <h2 className="text-lg font-bold" style={{ color: 'var(--ih-theme-text-primary)', fontFamily: 'var(--ih-font-family-heading)' }}>Computed Route Intelligence</h2>
                  <p className="text-sm" style={{ color: 'var(--ih-theme-text-muted)' }}>Execution Status: {status.toUpperCase()}</p>
                </div>
                <button
                  onClick={() => setShowMap(true)}
                  className="px-4 py-2 text-xs font-bold rounded-lg transition-colors flex items-center gap-2"
                  style={{ color: 'var(--ih-theme-accent-light)', backgroundColor: 'rgba(59, 130, 246, 0.1)', borderWidth: '1px', borderColor: 'var(--ih-theme-accent)' }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg>
                  View Warehouses
                </button>
              </div>

              <div className="flex-1 p-0 flex flex-col min-h-[400px]">
                {/* IDLE State */}
                {status === AppStatus.IDLE && (
                  <div className="flex-1 flex flex-col items-center justify-center text-center p-12">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: 'var(--ih-theme-bg-elevated)' }}>
                      <svg className="w-8 h-8" style={{ color: 'var(--ih-theme-text-muted)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                    </div>
                    <h3 className="font-semibold mb-1" style={{ color: 'var(--ih-theme-text-primary)', fontFamily: 'var(--ih-font-family-heading)' }}>System Standby</h3>
                    <p className="text-sm max-w-xs mx-auto" style={{ color: 'var(--ih-theme-text-muted)' }}>Input asset parameters and click Compute to trigger the routing logic.</p>
                    <button
                      onClick={() => setShowMap(true)}
                      className="mt-6 px-6 py-2.5 text-sm font-semibold rounded-lg transition-colors shadow-md flex items-center gap-2"
                      style={{ backgroundColor: 'var(--ih-theme-accent)', color: 'var(--ih-theme-white)' }}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg>
                      View Warehouse Map
                    </button>
                  </div>
                )}

                {/* LOADING State */}
                {status === AppStatus.LOADING && (
                  <div className="flex-1 flex flex-col items-center justify-center text-center p-12" style={{ backgroundColor: 'rgba(var(--ih-theme-bg-secondary-rgb), 0.5)' }}>
                    <div className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin mb-6" style={{ borderColor: 'var(--ih-theme-accent)', borderTopColor: 'transparent' }}></div>
                    <h3 className="font-semibold mb-2" style={{ color: 'var(--ih-theme-text-primary)', fontFamily: 'var(--ih-font-family-heading)' }}>Executing Routing Protocol</h3>
                    <div className="space-y-2 max-w-sm w-full">
                      {['Validating Inputs...', 'Generating Loop...', 'Fetching Request Details...'].map((step, idx) => (
                        <div key={idx} className="flex items-center gap-3 text-xs font-medium animate-pulse" style={{ color: 'var(--ih-theme-text-muted)' }}>
                          <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--ih-theme-text-muted)' }}></div>
                          {step}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* REQUEST_DETAILS State — Show request info + "Get Loop Details" button */}
                {status === AppStatus.REQUEST_DETAILS && requestDetails && (
                  <div className="flex-1 flex flex-col p-6 gap-6">
                    <div className="rounded-xl p-5" style={{ backgroundColor: 'rgba(16, 185, 129, 0.08)', borderWidth: '1px', borderColor: 'rgba(16, 185, 129, 0.2)' }}>
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(16, 185, 129, 0.15)', color: 'var(--ih-theme-success)' }}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                        </div>
                        <h3 className="font-bold text-sm" style={{ color: 'var(--ih-theme-success-light)', fontFamily: 'var(--ih-font-family-heading)' }}>Loop Generated Successfully</h3>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="rounded-lg p-3" style={{ backgroundColor: 'var(--ih-theme-bg-elevated)', borderWidth: '1px', borderColor: 'var(--ih-theme-border-light)' }}>
                          <p className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: 'var(--ih-theme-text-muted)' }}>Vehicle ID</p>
                          <p className="text-sm font-semibold" style={{ color: 'var(--ih-theme-text-primary)', fontFamily: 'var(--ih-font-family-mono)' }}>{requestDetails.vehicleId}</p>
                        </div>
                        <div className="rounded-lg p-3" style={{ backgroundColor: 'var(--ih-theme-bg-elevated)', borderWidth: '1px', borderColor: 'var(--ih-theme-border-light)' }}>
                          <p className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: 'var(--ih-theme-text-muted)' }}>Driver Name</p>
                          <p className="text-sm font-semibold truncate" title={requestDetails.driverId} style={{ color: 'var(--ih-theme-text-primary)' }}>{requestDetails.driverId}</p>
                        </div>
                        <div className="rounded-lg p-3" style={{ backgroundColor: 'var(--ih-theme-bg-elevated)', borderWidth: '1px', borderColor: 'var(--ih-theme-border-light)' }}>
                          <p className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: 'var(--ih-theme-text-muted)' }}>Vehicle Location</p>
                          <p className="text-sm font-semibold" style={{ color: 'var(--ih-theme-text-primary)' }}>{requestDetails.truckSource?.city}, {requestDetails.truckSource?.cityDistrict}</p>
                          <p className="text-sm font-semibold" style={{ color: 'var(--ih-theme-text-secondary)', fontFamily: 'var(--ih-font-family-mono)' }}>{requestDetails.vehicleLat}, {requestDetails.vehicleLong}</p>
                        </div>
                        <div className="rounded-lg p-3" style={{ backgroundColor: 'var(--ih-theme-bg-elevated)', borderWidth: '1px', borderColor: 'var(--ih-theme-border-light)' }}>
                          <p className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: 'var(--ih-theme-text-muted)' }}>Driver Location</p>
                          <p className="text-sm font-semibold" style={{ color: 'var(--ih-theme-text-primary)' }}>{requestDetails.driverSource?.city}, {requestDetails.driverSource?.cityDistrict}</p>
                          <p className="text-sm font-semibold" style={{ color: 'var(--ih-theme-text-secondary)', fontFamily: 'var(--ih-font-family-mono)' }}>{requestDetails.driverLat}, {requestDetails.driverLong}</p>
                        </div>
                        <div className="rounded-lg p-3" style={{ backgroundColor: 'var(--ih-theme-bg-elevated)', borderWidth: '1px', borderColor: 'var(--ih-theme-border-light)' }}>
                          <p className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: 'var(--ih-theme-text-muted)' }}>Loop Count</p>
                          <p className="text-sm font-semibold" style={{ color: 'var(--ih-theme-text-primary)', fontFamily: 'var(--ih-font-family-mono)' }}>{requestDetails.count}</p>
                        </div>
                        <div className="rounded-lg p-3" style={{ backgroundColor: 'var(--ih-theme-bg-elevated)', borderWidth: '1px', borderColor: 'var(--ih-theme-border-light)' }}>
                          <p className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: 'var(--ih-theme-text-muted)' }}>Updated Time</p>

                          <p className="text-sm font-semibold" style={{ color: 'var(--ih-theme-text-primary)', fontFamily: 'var(--ih-font-family-mono)' }}>
                            {new Date(requestDetails.updatedTime).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}, {new Date(requestDetails.updatedTime).getFullYear()} {new Date(requestDetails.updatedTime).toLocaleTimeString('en-GB')}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className='my-2'>
                      <p className='text-sm font-semibold' style={{ color: 'var(--ih-theme-warning)' }}>Please wait for 5 minutes to get loop generated.</p>
                    </div>

                    <button
                      onClick={handleGetLoopDetails}
                      disabled={loadingLoopDetails}
                      className="w-full py-3.5 px-4 rounded-lg font-bold text-sm transition-all flex items-center justify-center gap-2"
                      style={{
                        backgroundColor: loadingLoopDetails ? 'var(--ih-theme-bg-elevated)' : 'var(--ih-theme-accent)',
                        color: loadingLoopDetails ? 'var(--ih-theme-text-muted)' : 'var(--ih-theme-white)',
                        cursor: loadingLoopDetails ? 'not-allowed' : 'pointer',
                        boxShadow: loadingLoopDetails ? 'none' : '0 4px 14px rgba(59, 130, 246, 0.3)',
                      }}
                    >
                      {loadingLoopDetails ? (
                        <>
                          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Fetching Loop Details...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                          Get Loop Details
                        </>
                      )}
                    </button>

                    {/* Loop Details Table — renders below when available */}
                    {results.length > 0 && (
                      <div className="pt-4" style={{ borderTopWidth: '1px', borderColor: 'var(--ih-theme-border-light)' }}>
                        <h3 className="text-sm font-bold uppercase tracking-wider mb-3" style={{ color: 'var(--ih-theme-text-secondary)', fontFamily: 'var(--ih-font-family-heading)' }}>Loop Details</h3>
                        <RouteTable routes={results} />
                      </div>
                    )}
                  </div>
                )}

                {/* ERROR State */}
                {status === AppStatus.ERROR && (
                  <div className="flex-1 flex flex-col items-center justify-center p-12">
                    <div className="max-w-md w-full">
                      <div className="rounded-xl p-6 text-center shadow-sm" style={{ backgroundColor: 'rgba(239, 68, 68, 0.08)', borderWidth: '1px', borderColor: 'rgba(239, 68, 68, 0.2)' }}>
                        <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(239, 68, 68, 0.15)', color: 'var(--ih-theme-error)' }}>
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        </div>
                        <h3 className="font-bold mb-2" style={{ color: 'var(--ih-theme-error-light)', fontFamily: 'var(--ih-font-family-heading)' }}>Computation Rejected</h3>
                        <p className="text-sm font-medium mb-4" style={{ color: 'var(--ih-theme-error)' }}>{errorMsg}</p>
                        <button
                          onClick={() => setStatus(AppStatus.IDLE)}
                          className="text-xs px-4 py-2 rounded-lg font-bold transition-colors"
                          style={{ backgroundColor: 'var(--ih-theme-error)', color: 'var(--ih-theme-white)' }}
                        >
                          Retry with new parameters
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* SUCCESS State — Route Table */}
                {status === AppStatus.SUCCESS && results.length > 0 && (
                  <div className="w-full h-full flex flex-col">
                    <RouteTable routes={results} />
                    {logs.length > 0 && (
                      <div className="mt-auto p-4" style={{ borderTopWidth: '1px', borderColor: 'var(--ih-theme-border-light)', backgroundColor: 'var(--ih-theme-bg-secondary)' }}>
                        <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--ih-theme-text-muted)' }}>Processing Trace:</p>
                        <div className="flex gap-4 overflow-x-auto pb-2">
                          {logs.map((log, i) => (
                            <div key={i} className="whitespace-nowrap rounded px-2 py-1 text-[9px] font-medium" style={{ backgroundColor: 'var(--ih-theme-bg-elevated)', borderWidth: '1px', borderColor: 'var(--ih-theme-border-light)', color: 'var(--ih-theme-text-muted)' }}>
                              Step {i + 1}: {log}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="flex-1 animate-in zoom-in-95 duration-300">
          <LogicExplorer />
        </div>
      )}
    </Layout>
  );
};

export default App;
